import { z } from 'zod'

// ─── Shared ───────────────────────────────────────────────────────────────────

import { HazardCatalog, HazardProjection } from '@app/domains/hazards/types/hazard.types'
import { LayerMetadataRecord } from '@app/types'

export type IconRef = string
export type TilesetType = 'vector' | 'line' | 'point' | 'icon' | string
export type MetricType = 'count' | 'area' | 'length' | string
export type FilterableInformationType = 'text' | 'numeric' | string

// ─── Elements ─────────────────────────────────────────────────────────────────

export type ElementTileset = {
  id: string
  icon: IconRef | null
  type: TilesetType
  style: string | null
  sourceLayer: string
  has_extrusion: boolean
}

export type FilterableInformationField = {
  key: string
  type: FilterableInformationType
  label: string
  lookup?: boolean
}

export type ElementItem = {
  icon: IconRef
  type: string
  display_name: string
  legend: string
  unit: string
  metric_type: MetricType
  source: string
  provider: string
  date_added: string
  date_updated: string
  helpFileName: string | null
  tilesets: ElementTileset[]
  filterable_information: FilterableInformationField[] | null
  [k: string]: unknown
}

export type ElementGroup = {
  icon: IconRef
  groupName: string
  assets: ElementItem[]
}

export type ElementCategory = {
  icon: IconRef
  title: string
  groups: ElementGroup[]
}

export type ElementCatalog = Record<string, ElementCategory>

// ─── Contextuals ──────────────────────────────────────────────────────────────

export type ContextualTilesetType = 'vector' | 'point' | 'line' | string

export type ContextualTileset = {
  id: string
  icon: IconRef | null
  type: ContextualTilesetType
  sourceLayer: string
}

export type ContextualLayer = {
  id: string
  icon: IconRef
  legend: string
  tilesets: ContextualTileset[]
  display_name: string
}

export type ContextualGroup = {
  icon: IconRef
  groupName: string
  layers: ContextualLayer[]
}

export type ContextualCategory = {
  icon: IconRef
  title: string
  groups: ContextualGroup[]
}

export type ContextualCatalog = Record<string, ContextualCategory>

// ─── Catalog ──────────────────────────────────────────────────────────────────

export interface Layers {
  elements: ElementCatalog
  hazards: {
    hazardDetails: HazardCatalog
    projections: HazardProjection[]
  }
  contextuals: ContextualCatalog
  layerMetadata: LayerMetadataRecord[]
}
// ─── Layer detail ─────────────────────────────────────────────────────────────

export interface LayerSection {
  title: string
  description: string
}

export interface LayerMetaDetail {
  label: string
  value: string
}

export interface LayerImage {
  url?: string
  title?: string
  description?: string
}

export interface LayerAccordion {
  title?: string
  content?: string
}

export interface LayerSource {
  title?: string
  url?: string
}

export interface LayerDetail {
  title: string
  icon: string
  startsOpen: boolean
  images: LayerImage[]
  sections: LayerSection[]
  accordions: LayerAccordion[]
  details: LayerMetaDetail[]
  sources: LayerSource[]
  layer_id: string
}

export const layerMetadataSchema = z.object({
  title: z.string().optional(),
  description_md: z.string().optional(),
  provider: z.string().optional(),
  spatial_extent: z.string().optional(),
  created_at: z.string().optional(),
  owner_name: z.string().optional(),
  owner_organization: z.string().optional(),
  owner_email: z.string().email('Must be a valid email').optional().or(z.literal('')),
  confidence_rating: z.enum(['A', 'B', 'C', 'D']).optional().or(z.literal('')),
  limitations_caveats_md: z.string().optional(),
  source_links: z
    .array(
      z.object({
        title: z.string().min(1, 'Title is required'),
        url: z.string().url('Must be a valid URL'),
      }),
    )
    .optional(),
  license: z
    .object({
      title: z.string().optional(),
      url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
    })
    .optional(),
  additional_notes_md: z.string().optional(),
  asset_class: z.string().optional().nullable(),
  asset_group: z.string().optional().nullable(),
})

export type LayerMetadataFormValues = z.infer<typeof layerMetadataSchema>

export type VulnBucket = {
  total: number
  low_vulnerability: number
  medium_vulnerability: number
  high_vulnerability: number
  potential_vulnerability: number
  exposed_vulnerability: number
  unspecified_vulnerability: number
  insignificant_vulnerability: number
}

export type VulnBucketMissingAttributes = {
  total_missing_attributes: number | null
  low_vulnerability_missing: number | null
  medium_vulnerability_missing: number | null
  high_vulnerability_missing: number | null
  potential_vulnerability_missing: number | null
  exposed_vulnerability_missing: number | null
  unspecified_vulnerability_missing: number | null
  insignificant_vulnerability_missing: number | null
}

export type VulnBucketCounts = {
  total_count: number | null
  low_vulnerability_count: number | null
  medium_vulnerability_count: number | null
  high_vulnerability_count: number | null
  potential_vulnerability_count: number | null
  exposed_vulnerability_count: number | null
  unspecified_vulnerability_count: number | null
  insignificant_vulnerability_count: number | null
}

export interface AssetHazardExposureStats {
  total_metric: number
  bucket_size: number
  exposure_buckets: ({
    exposure_bucket: string
  } & VulnBucket)[]
  n_days_buckets: ({
    n_days: number
  } & VulnBucket)[]
  totals: VulnBucket
  assets_missing_attributes: VulnBucketMissingAttributes
  asset_counts: VulnBucketCounts
}

export interface RiskData {
  asset_type: string
  assetDisplayName: string
  hazard_scenario: string
  hazardScenarioDisplayName: string
  identifying_information_property: string | undefined
  data: AssetHazardExposureStats
  attribute_stats: AssetAttributeStats
}

export interface MultiRiskData {
  asset_type: string
  total_metric: number
  total_metric_exposed: number
  low_vulnerability: number
  medium_vulnerability: number
  high_vulnerability: number
  potential_vulnerability: number
  exposed_vulnerability: number
  unspecified_vulnerability: number
  insignificant_vulnerability: number
}

export interface MultiRiskDataObject {
  [key: string]: MultiRiskData
}

export interface AssetAttributeStats {
  replacement_costs: AssetStats
}

export interface AssetStats {
  total: MinMaxValue
  potential: MinMaxValue
  insignificant: MinMaxValue
  low: MinMaxValue
  medium: MinMaxValue
  high: MinMaxValue
  exposed: MinMaxValue
  unspecified: MinMaxValue
}

export interface MinMaxValue {
  min?: number
  value: number
  max?: number
}

export type IdentifyingInformation = {
  name: string
  property: string
  suffix?: string
}

export type CombinedRiskData = {
  individual_exposure: RiskData[]
  multihazard_exposure: MultiRiskDataObject
}

export type RiskFetcherIsLoading = {
  multiHazards: string[]
  perHazard: { [key: string]: string[] }
}

export type RiskPerHazard = {
  [key: string]: {
    // assetType
    [key: string]: {
      // scenario
      [key: string]: RiskData | null // attribute
    }
  }
}

export type RiskMultiHazard = {
  [key: string]: MultiRiskData | null
}

export interface RiskContextProps {
  riskByAssetPerHazard: RiskPerHazard
  riskByAssetMultiHazard: RiskMultiHazard
  isLoading: RiskFetcherIsLoading
  availableIdentifyingInformation: { [key: string]: IdentifyingInformation[] }
  setIdentifyingInformationForAssetHazard: (
    assetType: string,
    scenario: string,
    attribute: string,
  ) => void
  selectedIdentifyingInformation: {
    [key: string]: { [key: string]: IdentifyingInformation | undefined }
  }
}

export type RiskIsLoadingObject = {
  multiHazards: string[]
  perHazard: { [key: string]: string[] }
}
